/* eslint-disable no-param-reassign */
import moment from 'moment'

import { STATUSES, STATUSES_CLINIC } from '~/constants/statuses'

/**
 * Deep merges two objets.
 * @param  {Object} object destination object
 * @param  {Object} source source obejct
 *
 * @returns {Object} new object
 */
export const merge = (obj: any, source: any): any => {
  if (obj === source) return obj
  const newValue: any = {
    ...obj,
    ...source
  }

  Object.entries(source).forEach(([key, value]) => {
    newValue[key] =
      obj[key] && typeof obj[key] === 'object' ? merge(obj[key], value) : value
  })

  return newValue
}

/**
 * Return a copy of an object excluding the given key, or array of keys.
 *
 * @param {object} obj - initial object
 * @param {(string|string[])} props - values to be omitted
 * @param {function} [fn] - an optional filter function
 */
export function omit(obj: any, props: any, fn?: any): any {
  if (!obj || obj.constructor !== Object) return {}
  if (typeof props === 'function') {
    fn = props
    props = []
  }
  if (typeof props === 'string') {
    props = [props]
  }
  const isFunction = typeof fn === 'function'
  const keys = Object.keys(obj)
  const res: any = {}
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    const val = obj[key]
    if (
      !props ||
      (props.indexOf(key) === -1 && (!isFunction || fn(val, key, obj)))
    ) {
      res[key] = val
    }
  }
  return res
}

/**
 * Creates options for Autocomplete
 *
 * @param {array} data - data array
 * @param {string} key1 - object key1
 * @param {string} key2 - object key2
 */
export const createOptions = (
  data: Array<{
    [key: string]: any
  }>,
  key1 = 'id',
  key2 = 'name'
): {
  value: string
  label: string
  [key: string]: string
}[] => {
  if (!data || !Array.isArray(data)) return []
  return data.filter(Boolean).map((item) => ({
    value: item[key1],
    label: item[key2],
    ...item
  }))
}

/**
 * Change date format
 *
 * @param {string} date - string
 * @param {string} format - format date string from moment.js
 */
export const humanizeDate = (date: string, format = 'DD.MM.YYYY'): string => {
  return moment(date).format(format)
}

/**
 * Uppercase the first letter of a string
 *
 * @param {string} string - string
 */
export const capitalize = (string: string): string => {
  if (typeof string !== 'string') return ''
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Sort array by date
 *
 * @param {array} options - array
 * @param {string} field - field by which to sort
 * @param {boolean} isDesc - sort in reverse order
 */
export const sortByDate = <T = Option>(
  options: T[],
  field: string,
  isDesc?: boolean
): T[] => {
  return options
    ?.filter(Boolean)
    .slice()
    .sort((elementA, elementB) => {
      const dateA = new Date(elementA[field])
      const dateB = new Date(elementB[field])

      if (isDesc) {
        return ((dateB as unknown) as number) - ((dateA as unknown) as number)
      }
      return ((dateA as unknown) as number) - ((dateB as unknown) as number)
    })
}

/**
 * Check on validate query status
 *
 * @param {string} query
 * @returns {string} status
 */
export const validateQueryStatus = (query: string): string => {
  if (
    [
      STATUSES.new,
      STATUSES.canceled,
      STATUSES.scheduled,
      STATUSES.completed,
      STATUSES_CLINIC.confirmed,
      STATUSES_CLINIC.unconfirmed,
      STATUSES_CLINIC.archived
    ].includes(query)
  ) {
    return query
  }

  return ''
}

/**
 * Download image
 *
 * @param {string} src
 * @param {name} name
 */
export const downloadImage = (src: string, name?: string): void => {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = src
  img.onload = (): void => {
    // create Canvas
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = img.width
    canvas.height = img.height
    ctx.drawImage(img, 0, 0)

    // create <a> tag
    const a = document.createElement('a')
    a.download = `${name}.jpeg`
    a.href = canvas.toDataURL('image/jpeg')
    a.click()
  }
}
